import React from 'react';
import { cn } from '../lib/utils';
import { User, ExternalLink } from '../icons';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { contentKindLabel, contentKindAspect } from '../campaign-detail/utils/campaignDetailHelpers';
import { isSafeReferenceUrl } from '../campaign-detail/components/campaignDetailsParts';
import type { CountDisplay } from '../lib/engagementCount';
import { EngagementCountCell } from './EngagementCountCell';

// Shared cap for the popup's media area so portrait content (reels / shorts /
// posts) can't outgrow the viewport-capped dialog — one source of truth for
// both platform renderers.
const MEDIA_MAX_H = 'max-h-[70vh]';

const getYouTubeEmbedUrl = (url: string): string => {
  try {
    const u = new URL(url);
    const v = u.searchParams.get('v');
    if (v) return `https://www.youtube.com/embed/${v}`;
    const id = u.pathname.split('/').filter(Boolean).pop();
    if (id) return `https://www.youtube.com/embed/${id}`;
  } catch { /* malformed URL */ }
  return '';
};

interface PlatformRendererProps {
  url: string;
  thumbnailUrl?: string;
  contentKind?: string;
}

/**
 * Per-platform content area renderers. Add one entry here for each new
 * platform — no changes needed elsewhere in this file.
 *
 * Return `null` if the platform has no content to render (e.g. the URL
 * is missing or the embed URL can't be constructed).
 */
const PLATFORM_RENDERERS: Record<
  string,
  (props: PlatformRendererProps) => React.ReactNode
> = {
  youtube: ({ url, contentKind }) => {
    const embedUrl = getYouTubeEmbedUrl(url);
    if (!embedUrl) return null;
    return (
      <div className={cn(contentKindAspect(contentKind), MEDIA_MAX_H, 'overflow-hidden bg-black')}>
        <iframe
          src={embedUrl}
          className="size-full border-none"
          title="Live content"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  },

  instagram: ({ url, thumbnailUrl, contentKind }) => url && isSafeReferenceUrl(url) ? (
    <div
      className={cn('relative flex items-center justify-center overflow-hidden bg-stone-900', MEDIA_MAX_H, contentKindAspect(contentKind))}
      style={{ minHeight: 280 }}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          className="absolute inset-0 size-full object-contain opacity-50"
        />
      )}
      <div className="relative flex flex-col items-center gap-4 py-14">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-body-sm-semibold text-foreground transition-colors hover:bg-stone-50"
        >
          <ExternalLink size={16} />
          Watch {contentKindLabel(contentKind)} on Instagram
        </a>
      </div>
    </div>
  ) : null,

  // X blocks iframing, so the popup links out rather than rendering empty.
  x: ({ url, contentKind }) => url && isSafeReferenceUrl(url) ? (
    <div
      className="relative flex items-center justify-center bg-stone-900"
      style={{ minHeight: 280 }}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-body-sm-semibold text-foreground transition-colors hover:bg-stone-50"
      >
        <ExternalLink size={16} />
        View {contentKindLabel(contentKind)} on X
      </a>
    </div>
  ) : null,
};

export interface VideoPlayerPopupProps {
  videoUrl: string;
  /** Platform thumbnail — shown as background for Instagram content. */
  thumbnailUrl?: string;
  creatorName: string;
  creatorHandle?: string;
  creatorAvatarUrl?: string;
  /** Campaign platform — selects the renderer from PLATFORM_RENDERERS. */
  platform: string;
  /** BE content_kind: reel | post | short | video. Drives aspect ratio and CTA label. */
  contentKind?: string;
  views: CountDisplay;
  likes: CountDisplay;
  comments: CountDisplay;
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export function VideoPlayerPopup({
  videoUrl,
  thumbnailUrl,
  creatorName,
  creatorHandle,
  creatorAvatarUrl,
  platform,
  contentKind,
  views,
  likes,
  comments,
  onClose,
  onCloseAutoFocus,
}: VideoPlayerPopupProps) {
  const renderContent = PLATFORM_RENDERERS[platform];
  const kindLabel = contentKindLabel(contentKind);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* Modal shell (Radix Dialog): scroll-locks + inerts the background and
          traps focus; scrim-click / Escape close. Flush p-0 so the header,
          media, and stats own their spacing. */}
      <DialogContent
        showCloseButton
        onCloseAutoFocus={onCloseAutoFocus}
        className="flex max-h-[90vh] max-w-[720px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[720px]"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-3.5 pr-12">
          <div className="size-10 shrink-0 overflow-hidden rounded-full border border-border bg-muted">
            {creatorAvatarUrl
              ? <img src={creatorAvatarUrl} alt="" className="size-full object-cover" />
              : <div className="flex size-full items-center justify-center">
                  <User size={18} className="text-muted-foreground" />
                </div>
            }
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-body-md-semibold text-foreground">
              {creatorName}
            </DialogTitle>
            <div className="mt-0.5 flex items-center gap-1.5">
              {creatorHandle && (
                <span className="text-caption-regular text-muted-foreground">
                  @{creatorHandle.replace(/^@/, '')}
                </span>
              )}
              {contentKind && (
                <span className="rounded-md bg-muted px-1.5 py-0.5 text-caption-medium text-muted-foreground">
                  {kindLabel}
                </span>
              )}
            </div>
          </div>
          {videoUrl && isSafeReferenceUrl(videoUrl) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.open(videoUrl, '_blank')}
              className="text-caption-medium"
            >
              <ExternalLink size={13} /> Open
            </Button>
          )}
        </div>

        {/* Platform content area — min-h-0 lets it shrink inside the
            max-h-[90vh] dialog so a tall (portrait) reel can't push the stats
            footer past the dialog's bottom edge on a short window. */}
        <div className="min-h-0 overflow-hidden">
          {renderContent?.({ url: videoUrl, thumbnailUrl, contentKind })}
        </div>

        {/* Stats footer */}
        <div className="grid shrink-0 grid-cols-3 border-t border-border">
          {[
            { label: 'Views', count: views },
            { label: 'Likes', count: likes },
            { label: 'Comments', count: comments },
          ].map((s, i) => (
            <div
              key={s.label}
              className={cn('px-3 py-3.5 text-center', i < 2 && 'border-r border-border')}
            >
              <div className="mb-1 text-caption-medium uppercase tracking-wider text-muted-foreground">
                {s.label}
              </div>
              <div className="text-heading-h7 text-foreground">
                <EngagementCountCell count={s.count} className="items-center" />
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
